import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { FaqItemDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { Reveal } from '../Reveal';

export function Faq({ heading, items }: { heading: string; items: FaqItemDto[] }) {
  if (!items.length) return null;
  return (
    <section aria-labelledby="faq-title" className="container-site relative z-10">
      <div aria-hidden className="h-px bg-line" />
      <Reveal className="pt-16 md:pt-[85px]">
        <h2 id="faq-title" className="display-xl max-w-[7ch] text-strong">
          {keepLastWordsTogether(heading)}
        </h2>
      </Reveal>
      <ul className="mt-10 flex flex-col gap-4 md:mt-[78px] md:gap-[33px]">
        {items.map((item, i) => (
          <FaqRow key={item.id} item={item} pinned={i === items.length - 1} />
        ))}
      </ul>
    </section>
  );
}

/** Joins the last two words with a no-break space so the heading wraps as "Before / we begin." */
const keepLastWordsTogether = (text: string) => text.replace(/\s+(\S+)\s*$/, ' $1');

function FaqRow({ item, pinned }: { item: FaqItemDto; pinned: boolean }) {
  // Answers start collapsed and toggle on click; the pinned (last) row is always open.
  const [expanded, setExpanded] = useState(false);
  const open = pinned || expanded;
  const reduce = useReducedMotion();
  const panelId = `faq-${item.id}`;
  return (
    <Reveal
      as="li"
      className="rounded-[28px] bg-card shadow-[0_30px_50px_-22px_rgb(30_30_30/0.3)] md:rounded-[40px]"
    >
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          disabled={pinned}
          onClick={() => setExpanded((o) => !o)}
          className={cn(
            'group flex disabled:cursor-default w-full items-start justify-between gap-6 rounded-[28px] px-6 pt-7 text-start transition-[padding] duration-300 md:rounded-[40px] md:ps-12 md:pe-[57px] md:pt-[36px]',
            open ? 'pb-1' : 'pb-7 md:pb-[40px]',
          )}
        >
          <span className="max-w-[9.4em] text-h2 font-bold text-balance text-strong">
            {item.question}
          </span>
          {!pinned && (
            <span
              aria-hidden
              className="relative mt-[0.55em] block h-1 w-7 shrink-0 transition-transform duration-300 group-hover:scale-110 md:mt-[30px] md:h-[5px] md:w-9"
            >
              <span className="absolute inset-0 rounded-full bg-primary" />
              <span
                className={cn(
                  'absolute inset-0 rounded-full bg-primary transition-transform duration-300',
                  open ? 'rotate-0' : 'rotate-90',
                )}
              />
            </span>
          )}
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="max-w-[968px] px-6 pt-4 pb-8 text-body text-graphite md:ps-12 md:pe-[57px] md:pt-[22px] md:pb-[46px]">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </Reveal>
  );
}
